/**
 * Wrapper sopra Google Places API (new) per autocompletamento indirizzi e ricerca aziende.
 *
 * Usa la NUOVA API Places (AutocompleteSuggestion + Place) raccomandata da Google
 * da marzo 2025. La vecchia AutocompleteService/PlacesService e' deprecata
 * per nuovi clienti.
 *
 * Uso 2 entry points:
 *  - searchPlaces(query, ['establishment']): cerca aziende/POI per nome (es. "Vivisolar")
 *  - searchPlaces(query, ['address']): cerca indirizzi (es. "Via Sigmund Freud 4 Cagliari")
 *
 * Selezione di un risultato -> getPlaceDetails(place_id) -> struttura indirizzo completa.
 *
 * Cost optimization: usa session tokens (Google fattura solo Place Details, non
 * gli Autocomplete fatti nella stessa sessione).
 *
 * Normalizzazione province italiane: dopo la riforma 2016 alcune sigle sono state
 * soppresse (VS, CI, OG, OT). Google puo' ancora restituire le vecchie sigle:
 * vengono rimappate a quelle correnti prima di esporle al chiamante.
 *
 * Vedi: https://developers.google.com/maps/documentation/javascript/places-autocomplete
 *       https://developers.google.com/maps/documentation/javascript/place-class
 *
 * Nota tecnica: @googlemaps/js-api-loader v2 ha deprecato la classe `Loader` legacy in
 * favore della nuova API funzionale `setOptions()` + `importLibrary()`.
 */

import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;

let optionsConfigured = false;
let placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null;

async function loadPlacesLibrary(): Promise<google.maps.PlacesLibrary> {
  if (!optionsConfigured) {
    setOptions({
      key: API_KEY,
      v: 'weekly',
      libraries: ['places'],
      language: 'it',
      region: 'IT',
    });
    optionsConfigured = true;
  }
  if (!placesLibraryPromise) {
    placesLibraryPromise = importLibrary('places');
  }
  return placesLibraryPromise;
}

export interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
}

export interface PlaceDetailsResult {
  placeId: string;
  name?: string;
  formattedAddress?: string;
  via?: string;
  numeroCivico?: string;
  cap?: string;
  citta?: string;
  provinciaSigla?: string;
  regione?: string;
  paese?: string;
  lat?: number;
  lng?: number;
}

// ============================================================================
// Session token management
// ============================================================================
// In Places API New, AutocompleteSessionToken raggruppa una sessione di
// autocomplete (piu' keystroke -> un solo Place Details fetch fatturato).

let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

function getOrCreateSessionToken(
  placesLib: google.maps.PlacesLibrary,
): google.maps.places.AutocompleteSessionToken {
  if (!sessionToken) {
    sessionToken = new placesLib.AutocompleteSessionToken();
  }
  return sessionToken;
}

function resetSessionToken() {
  sessionToken = null;
}

// ============================================================================
// Italian province legacy sigle normalization
// ============================================================================
// Dopo la riforma 2016 alcune province sono state accorpate. Google puo'
// ancora restituire le vecchie sigle (es. VS per Villacidro). Normalizziamo
// PRIMA di esporre il valore al consumer.
//
//   VS (Medio Campidano)     -> SU (Sud Sardegna)
//   CI (Carbonia-Iglesias)   -> SU (Sud Sardegna)
//   OG (Ogliastra)           -> NU (Nuoro)
//   OT (Olbia-Tempio)        -> SS (Sassari)

const LEGACY_PROVINCIA_REMAP: Record<string, string> = {
  VS: 'SU',
  CI: 'SU',
  OG: 'NU',
  OT: 'SS',
};

export function normalizeProvinciaSigla(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const up = raw.toUpperCase();
  return LEGACY_PROVINCIA_REMAP[up] || up;
}

// ============================================================================
// Public API
// ============================================================================

export type PlaceType = 'establishment' | 'address' | 'geocode';

export async function searchPlaces(
  query: string,
  types: PlaceType[] = ['establishment'],
): Promise<PlaceSuggestion[]> {
  if (!query || query.trim().length < 2) return [];
  const placesLib = await loadPlacesLibrary();
  const { AutocompleteSuggestion } = placesLib;
  const token = getOrCreateSessionToken(placesLib);

  // Map dei nostri tipi semplificati ai primary types della nuova API.
  // 'address' nel vecchio mondo -> in Places New corrisponde a indirizzi
  // postali (street_address) o vie (route).
  let includedPrimaryTypes: string[] | undefined;
  if (types.includes('establishment') && !types.includes('address')) {
    includedPrimaryTypes = ['establishment'];
  } else if (types.includes('address') && !types.includes('establishment')) {
    includedPrimaryTypes = ['street_address', 'route', 'premise'];
  }
  // se richiesti entrambi (o 'geocode') => omettiamo per ottenere tutti i tipi

  const request: google.maps.places.AutocompleteRequest = {
    input: query,
    sessionToken: token,
    includedRegionCodes: ['IT'],
    language: 'it',
    region: 'IT',
    ...(includedPrimaryTypes ? { includedPrimaryTypes } : {}),
  };

  try {
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    if (!suggestions) return [];

    return suggestions
      .filter((s) => s.placePrediction)
      .map((s) => {
        const p = s.placePrediction!;
        return {
          placeId: p.placeId,
          mainText: p.mainText?.toString() ?? p.text?.toString() ?? '',
          secondaryText: p.secondaryText?.toString() ?? '',
          fullText: p.text?.toString() ?? '',
        };
      });
  } catch (err) {
    console.error('[GooglePlacesService] searchPlaces failed', err);
    return [];
  }
}

export async function getPlaceDetails(placeId: string): Promise<PlaceDetailsResult | null> {
  const placesLib = await loadPlacesLibrary();
  const { Place } = placesLib;

  try {
    const place = new Place({ id: placeId, requestedLanguage: 'it' });
    await place.fetchFields({
      fields: ['displayName', 'formattedAddress', 'addressComponents', 'location'],
    });

    // Sessione chiusa: il prossimo searchPlaces aprira' un nuovo token.
    resetSessionToken();

    const components: google.maps.places.AddressComponent[] = place.addressComponents ?? [];

    const findComponent = (type: string) =>
      components.find((c) => Array.isArray(c.types) && c.types.includes(type));

    const get = (type: string) => findComponent(type)?.longText ?? undefined;
    const getShort = (type: string) => findComponent(type)?.shortText ?? undefined;

    const via = get('route');
    const numeroCivico = get('street_number');
    const cap = get('postal_code');

    // PRIORITA' citta:
    //   1. administrative_area_level_3 (comune italiano, source of truth ISTAT)
    //   2. postal_town
    //   3. locality (puo' essere una sub-localita' tipo "Localita' Produttiva")
    //   4. administrative_area_level_2 (provincia, last resort)
    const citta =
      get('administrative_area_level_3') ||
      get('postal_town') ||
      get('locality') ||
      get('administrative_area_level_2');

    const provinciaRaw = getShort('administrative_area_level_2');
    const provinciaSigla = normalizeProvinciaSigla(provinciaRaw);
    const regione = get('administrative_area_level_1');
    const paese = getShort('country');

    const lat = typeof place.location?.lat === 'function' ? place.location.lat() : undefined;
    const lng = typeof place.location?.lng === 'function' ? place.location.lng() : undefined;

    return {
      placeId,
      name: place.displayName ?? undefined,
      formattedAddress: place.formattedAddress ?? undefined,
      via: via
        ? numeroCivico
          ? `${via}, ${numeroCivico}`
          : via
        : place.formattedAddress ?? undefined,
      numeroCivico,
      cap,
      citta,
      provinciaSigla,
      regione,
      paese,
      lat,
      lng,
    };
  } catch (err) {
    console.error('[GooglePlacesService] getPlaceDetails failed', err);
    return null;
  }
}
